import { useState, useRef, useEffect, useCallback } from "react";
import jsQR from "jsqr"; // ← npm install jsqr

// ============================================================
// QrScanner → Proxy FormAssembly (formId dinámico en la ruta)
//
// El QR contiene:  documento|idAE|formId|nombre
//                  ej: "12345678|15|5228366|María Pérez"
//
// ⚠️ Si el orden de tu QR es otro, ajustá los índices acá abajo
//    (QR_INDEX_*). Son 0-based.
//
// La petición al proxy queda:
//   POST {proxyUrl}/{formId}          ← el formId va EN LA RUTA
//   body (urlencoded): tfa_1=documento & tfa_3=idAE
//
// Los tokens tfa_db* NO se mandan: el proxy los obtiene frescos
// con su GET y pisa cualquier cosa que venga del front.
//
// Props (todas opcionales):
//   proxyUrl → endpoint base del proxy, SIN el formId (default abajo)
//   formId   → si se pasa, PISA el form ID leído del QR
//   idAE     → si se pasa, PISA el ID AE leído del QR
//   onResult → callback (parsed, respuestaProxy) tras éxito
// ============================================================

const QR_INDEX_DOCUMENTO = 0; // posición del documento (Cédula → tfa_1)
const QR_INDEX_ID_AE = 1;     // posición del ID AE (→ tfa_3)
const QR_INDEX_FORM_ID = 2;   // posición del form ID (→ ruta del proxy)
const QR_SEPARATOR = "|";

export default function QrScanner({
  proxyUrl = "https://pideapi2025.azurewebsites.net/api/FormAssemblyProxy/submit",
  formId = null,
  idAE = null,
  onResult = null,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const scanningRef = useRef(false);

  // status: idle | starting | scanning | calling | done | error
  const [status, setStatus] = useState("idle");
  const [parsed, setParsed] = useState(null); // { documento, idAE, formId, nombre, raw }
  const [apiResponse, setApiResponse] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // ---------- detener cámara ----------
  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => stopCamera, [stopCamera]); // cleanup al desmontar

  // ---------- parsear el contenido del QR ----------
  const parseQr = useCallback(
    (raw) => {
      const parts = raw.split(QR_SEPARATOR).map((p) => p.trim());
      const documento = parts[QR_INDEX_DOCUMENTO] || "";
      const idAEFromQr = parts[QR_INDEX_ID_AE] || "";
      const formIdFromQr = parts[QR_INDEX_FORM_ID] || "";
      const nombre = parts.length > 3 ? parts[parts.length - 1] : "";

      if (!documento) {
        throw new Error(
          `QR inválido: no se encontró el documento. Contenido: "${raw}"`
        );
      }

      return {
        raw,
        documento,
        idAE: idAE ?? idAEFromQr,       // la prop pisa al QR si se pasó
        formId: formId ?? formIdFromQr, // la prop pisa al QR si se pasó
        nombre,
      };
    },
    [idAE, formId]
  );

  // ---------- llamada al proxy ----------
  const callProxy = useCallback(
    async (data) => {
      setStatus("calling");
      try {
        if (!data.idAE) {
          throw new Error(
            "QR inválido: falta el ID AE y no se configuró uno por prop."
          );
        }
        if (!data.formId || !/^\d+$/.test(data.formId)) {
          throw new Error(
            `QR inválido: el form ID falta o no es numérico ("${data.formId}").`
          );
        }

        // IMPORTANTE: form data (urlencoded), NO JSON.
        // El proxy usa Request.ReadFormAsync() y rechaza JSON.
        const body = new URLSearchParams();
        body.set("tfa_1", data.documento); // Cédula
        body.set("tfa_3", data.idAE);      // ID AE

        // El formId va en la ruta: POST {proxyUrl}/{formId}
        const url = `${proxyUrl.replace(/\/$/, "")}/${encodeURIComponent(
          data.formId
        )}`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        });

        // El proxy responde JSON: { success, message }
        let payload = null;
        try {
          payload = await res.json();
        } catch (_) {
          /* respuesta no-JSON: la tratamos abajo */
        }

        if (!res.ok || !payload?.success) {
          throw new Error(
            payload?.message || `El proxy respondió ${res.status}`
          );
        }

        setApiResponse(payload);
        setStatus("done");
        if (onResult) onResult(data, payload);
      } catch (err) {
        setErrorMsg(err.message);
        setStatus("error");
      }
    },
    [proxyUrl, onResult]
  );

  // ---------- código detectado ----------
  const handleDetected = useCallback(
    (value) => {
      if (!scanningRef.current) return;
      scanningRef.current = false;
      stopCamera();
      try {
        const data = parseQr(value);
        setParsed(data);
        callProxy(data);
      } catch (err) {
        setParsed({ raw: value, documento: "", idAE: "", formId: "", nombre: "" });
        setErrorMsg(err.message);
        setStatus("error");
      }
    },
    [callProxy, parseQr, stopCamera]
  );

  // ---------- loop de escaneo ----------
  const scanLoop = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !scanningRef.current) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      // 1) BarcodeDetector nativo (Chrome / Android)
      if (detectorRef.current) {
        try {
          const codes = await detectorRef.current.detect(video);
          if (codes.length > 0) return handleDetected(codes[0].rawValue);
        } catch (_) {
          /* frame no procesable, seguimos */
        }
      } else {
        // 2) Fallback jsQR sobre canvas
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(img.data, img.width, img.height, {
          inversionAttempts: "dontInvert",
        });
        if (result && result.data) return handleDetected(result.data);
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [handleDetected]);

  // ---------- iniciar ----------
  const startScan = useCallback(async () => {
    setStatus("starting");
    setParsed(null);
    setApiResponse(null);
    setErrorMsg("");
    try {
      if ("BarcodeDetector" in window) {
        try {
          const formats = await window.BarcodeDetector.getSupportedFormats();
          if (formats.includes("qr_code")) {
            detectorRef.current = new window.BarcodeDetector({
              formats: ["qr_code"],
            });
          }
        } catch (_) {
          detectorRef.current = null; // usamos jsQR
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true"); // iOS
      await video.play();

      scanningRef.current = true;
      setStatus("scanning");
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch (err) {
      stopCamera();
      setErrorMsg(
        err.name === "NotAllowedError"
          ? "Permiso de cámara denegado. Habilitalo en el navegador y volvé a intentar."
          : err.message
      );
      setStatus("error");
    }
  }, [scanLoop, stopCamera]);

  const cancelScan = () => {
    stopCamera();
    setStatus("idle");
  };

  // ============================ UI ============================
  const S = styles;
  const isScanning = status === "starting" || status === "scanning";

  return (
    <div style={S.wrapper}>
      <div style={S.card}>
        <p style={S.eyebrow}>Check in — Eventos</p>
        <h2 style={S.title}>
          {status === "done"
            ? "Check-in registrado"
            : isScanning
            ? "Apuntá al código"
            : "Escanear entrada"}
        </h2>

        {/* Visor */}
        <div style={S.viewport}>
          <video ref={videoRef} muted style={S.video} />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {!isScanning && (
            <div style={S.overlayCenter}>
              {status === "idle" && (
                <span style={S.overlayText}>La cámara está apagada</span>
              )}
              {status === "calling" && (
                <span style={S.overlayText}>Registrando check-in…</span>
              )}
              {status === "done" && <span style={S.checkmark}>✓</span>}
              {status === "error" && <span style={S.crossmark}>✕</span>}
            </div>
          )}

          {isScanning && (
            <>
              <div style={S.frame} />
              <div style={S.scanLine} />
            </>
          )}
        </div>

        {/* Resultado */}
        {parsed && (
          <div style={S.resultBox}>
            {parsed.nombre && (
              <>
                <span style={S.resultLabel}>Participante</span>
                <span style={S.resultName}>{parsed.nombre}</span>
              </>
            )}
            <span style={S.resultLabel}>Cédula (tfa_1)</span>
            <code style={S.resultCode}>{parsed.documento || "—"}</code>
            <span style={S.resultLabel}>ID AE (tfa_3)</span>
            <code style={S.resultCode}>{parsed.idAE || "—"}</code>
            <span style={S.resultLabel}>Formulario destino</span>
            <code style={S.resultCode}>{parsed.formId || "—"}</code>
            {status === "done" && apiResponse?.message && (
              <>
                <span style={S.resultLabel}>Respuesta del proxy</span>
                <code style={S.resultCode}>{apiResponse.message}</code>
              </>
            )}
          </div>
        )}

        {status === "error" && <p style={S.errorText}>{errorMsg}</p>}

        {/* Acciones */}
        <div style={S.actions}>
          {isScanning ? (
            <button style={S.btnSecondary} onClick={cancelScan}>
              Cancelar
            </button>
          ) : (
            <button style={S.btnPrimary} onClick={startScan}>
              {status === "done" || status === "error"
                ? "Escanear otro"
                : "Iniciar escaneo"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- paleta ----------
const PALETTE = {
  teal: "#1B6B7C",
  turquoise: "#4AC3D6",
  lime: "#DCE02A",
  bg: "#FDFDFB",
  surface: "#FFFFFF",
  border: "#DDEBEE",
  textMuted: "#6E8C94",
  error: "#D65A4A",
};

// ---------- estilos ----------
const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: PALETTE.bg,
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: PALETTE.surface,
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 16,
    padding: 24,
    color: PALETTE.teal,
    boxShadow: "0 8px 24px rgba(27,107,124,0.10)",
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: PALETTE.turquoise,
    fontWeight: 700,
  },
  title: { margin: "6px 0 18px", fontSize: 22, fontWeight: 700 },
  viewport: {
    position: "relative",
    width: "100%",
    aspectRatio: "1 / 1",
    background: "#0c2a30",
    borderRadius: 12,
    overflow: "hidden",
  },
  video: { width: "100%", height: "100%", objectFit: "cover" },
  overlayCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(12,42,48,0.85)",
  },
  overlayText: { color: "#BFE4EA", fontSize: 14 },
  checkmark: { fontSize: 64, color: PALETTE.lime },
  crossmark: { fontSize: 64, color: PALETTE.error },
  frame: {
    position: "absolute",
    inset: "12%",
    border: `2px solid ${PALETTE.turquoise}`,
    borderRadius: 12,
    boxShadow: "0 0 0 9999px rgba(12,42,48,0.35)",
    pointerEvents: "none",
  },
  scanLine: {
    position: "absolute",
    left: "14%",
    right: "14%",
    top: "14%",
    height: 2,
    background: PALETTE.lime,
    boxShadow: `0 0 12px ${PALETTE.lime}`,
    animation: "qr-sweep 2s ease-in-out infinite alternate",
    pointerEvents: "none",
  },
  resultBox: {
    marginTop: 16,
    padding: 12,
    background: "#F2FAFB",
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  resultLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: PALETTE.textMuted,
    fontWeight: 700,
  },
  resultName: { fontSize: 18, fontWeight: 700, color: PALETTE.teal },
  resultCode: {
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    fontSize: 13,
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
    color: PALETTE.teal,
  },
  errorText: { color: PALETTE.error, fontSize: 14, marginTop: 12 },
  actions: { marginTop: 18, display: "flex", gap: 10 },
  btnPrimary: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 700,
    color: "#FFFFFF",
    background: PALETTE.teal,
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
  },
  btnSecondary: {
    flex: 1,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 700,
    color: PALETTE.teal,
    background: "transparent",
    border: `2px solid ${PALETTE.turquoise}`,
    borderRadius: 999,
    cursor: "pointer",
  },
};

// keyframes para la línea de barrido
if (
  typeof document !== "undefined" &&
  !document.getElementById("qr-sweep-kf")
) {
  const style = document.createElement("style");
  style.id = "qr-sweep-kf";
  style.textContent = `@keyframes qr-sweep { from { transform: translateY(0); } to { transform: translateY(min(60vw, 280px)); } }`;
  document.head.appendChild(style);
}